import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, MessageSquareOff, SearchX, UserX, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/EmptyState";
import KillSwitchBanner from "@/components/marketing/KillSwitchBanner";
import { ContactTiles } from "@/components/marketing/contacts/ContactTiles";
import { ContactsMonthCard } from "@/components/marketing/contacts/ContactsMonthCard";
import { ContactsToolbar } from "@/components/marketing/contacts/ContactsToolbar";
import { ContactsTable } from "@/components/marketing/contacts/ContactsTable";
import { ContactsPager } from "@/components/marketing/contacts/ContactsPager";
import { ContactDetailSheet } from "@/components/marketing/contacts/ContactDetailSheet";
import {
  MarketingExclusionDialog,
  type MarketingExclusionTarget,
} from "@/components/marketing/contacts/MarketingExclusionDialog";
import { NO_NAME } from "@/components/marketing/contacts/ContactsTable";
import { useSetMarketingExclusion } from "@/hooks/sms/useMarketingExclusion";
import { useToast } from "@/hooks/use-toast";
import { useMarketingSettings } from "@/hooks/sms/useMarketingSettings";
import { useContacts, useContactTileCounts } from "@/hooks/sms/useContacts";
import { useInviteMonthSummary } from "@/hooks/sms/useInviteMonthSummary";
import {
  DEFAULT_CONTACTS_SORT,
  hasActiveContactFilters,
  type ContactSortKey,
  type ContactSourceFilter,
  type ContactStatusFilter,
  type ContactsSort,
} from "@/utils/contacts";
import type { ContactListRow } from "@/types/sms";

const SEARCH_DEBOUNCE_MS = 250;

const SORT_CAPTIONS: Record<ContactSortKey, { asc: string; desc: string }> = {
  last_order_at: { asc: "oldest order first", desc: "newest order first" },
  display_name: { asc: "name A to Z", desc: "name Z to A" },
  opt_in_status: { asc: "subscribed first", desc: "not asked first" },
};

/**
 * The Contacts tab of /marketing: every customer with a valid mobile and their
 * text club state (subscribed, opted out, or not asked; opted-out people stay
 * listed), the four roster tiles, and the Order Invite month roll-up. Server
 * paged from v_customer_invite_status, 50 rows at a time. No add, import or
 * export; the one write is the per-customer "Don't send marketing texts"
 * exclusion (and its undo), confirmed first and saved by the sms-actions edge
 * function. Search, filter, sort and page live in
 * component state only, never the URL (names and numbers must not reach
 * browser history or request logs through the address bar).
 */
export default function ContactsTab() {
  const navigate = useNavigate();
  const { data: settings } = useMarketingSettings();
  const counts = useContactTileCounts();
  const month = useInviteMonthSummary();

  const [search, setSearch] = useState("");
  const [term, setTerm] = useState("");
  const [status, setStatus] = useState<ContactStatusFilter>("all");
  const [source, setSource] = useState<ContactSourceFilter>("all");
  const [sort, setSort] = useState<ContactsSort>(DEFAULT_CONTACTS_SORT);
  const [page, setPage] = useState(0);
  const [openContact, setOpenContact] = useState<ContactListRow | null>(null);
  const [exclusionTarget, setExclusionTarget] = useState<MarketingExclusionTarget | null>(null);
  const setExclusion = useSetMarketingExclusion();
  const { toast } = useToast();
  const listTop = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const id = setTimeout(() => {
      setTerm(search);
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [search]);

  const params = useMemo(() => ({ term, status, source, sort, page }), [term, status, source, sort, page]);
  const contacts = useContacts(params);

  const timezone = settings?.timezone ?? "America/Los_Angeles";
  const ebrWindowDays = settings?.invite_ebr_window_days ?? 180;
  const total = contacts.data?.total ?? 0;
  const rows = contacts.data?.rows ?? [];
  const filtered = hasActiveContactFilters({ term, status, source });

  const askToggleMarketing = contacts.data?.exclusionsAvailable
    ? (row: ContactListRow) =>
        setExclusionTarget({
          name: row.display_name?.trim() || NO_NAME,
          phone: row.customer_phone,
          excluded: row.marketing_excluded,
        })
    : undefined;

  const confirmToggleMarketing = (target: MarketingExclusionTarget) => {
    const excluded = !target.excluded;
    setExclusion.mutate(
      { customer_phone: target.phone, excluded },
      {
        onSuccess: (saved) => {
          setOpenContact((current) =>
            current && current.customer_phone === target.phone
              ? { ...current, marketing_excluded: saved.excluded, marketing_excluded_at: saved.excluded_at }
              : current,
          );
          toast(
            saved.excluded
              ? { title: `Marketing texts off for ${target.name}`, description: "Order confirmations still send." }
              : { title: `Marketing texts allowed for ${target.name}` },
          );
        },
        onError: (error) => {
          toast({ title: "Couldn't save that change", description: error.message, variant: "destructive" });
        },
      },
    );
  };

  const changeStatus = (next: ContactStatusFilter) => {
    setStatus(next);
    setPage(0);
  };
  const changeSource = (next: ContactSourceFilter) => {
    setSource(next);
    setPage(0);
  };
  const changeSort = (key: ContactSortKey) => {
    setSort((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: key === "last_order_at" ? "desc" : "asc" },
    );
    setPage(0);
  };
  const clearFilters = () => {
    setSearch("");
    setTerm("");
    setStatus("all");
    setSource("all");
    setPage(0);
  };

  const caption = contacts.isLoading
    ? "Loading contacts…"
    : `${total.toLocaleString()} contact${total === 1 ? "" : "s"} · ${SORT_CAPTIONS[sort.key][sort.direction]}`;

  const empty = () => {
    if (status === "opted_in" && !term && source === "all") {
      return (
        <EmptyState
          icon={CheckCircle2}
          title="Nobody has joined yet"
          description="Subscribers show up here as soon as someone texts JOIN or says yes to an invite."
        >
          <Button variant="outline" className="h-11" onClick={() => navigate("/marketing?tab=invitation")}>
            Go to Invitation
          </Button>
        </EmptyState>
      );
    }
    if (status === "opted_out" && !term && source === "all") {
      return (
        <EmptyState
          icon={UserX}
          title="Nobody has opted out"
          description="When someone replies STOP they stay listed here so they are never texted again."
        />
      );
    }
    if (status === "excluded" && !term && source === "all") {
      return (
        <EmptyState
          icon={MessageSquareOff}
          title="Nobody is excluded"
          description="Customers you stop marketing texts to show up here. Their order confirmations still send."
        />
      );
    }
    if (filtered) {
      return (
        <EmptyState
          icon={SearchX}
          title="No contacts match"
          description="Try a shorter name, or the last four digits of the number."
        >
          <Button variant="outline" className="h-11" onClick={clearFilters}>
            Clear filters
          </Button>
        </EmptyState>
      );
    }
    return (
      <EmptyState
        icon={Users}
        accent="primary"
        title="No customers yet"
        description="Customers appear here as orders come in from the register."
      />
    );
  };

  return (
    <div className="space-y-4">
      <KillSwitchBanner />

      <header>
        <h2 className="text-lg font-semibold text-foreground">Contacts</h2>
        <p className="mt-0.5 max-w-prose text-sm text-muted-foreground">
          Everyone who has ordered{settings?.store_name ? ` from ${settings.store_name}` : ""}, and whether they said
          yes to texts.
        </p>
      </header>

      <ContactTiles
        counts={counts.data}
        isLoading={counts.isLoading}
        isError={counts.isError}
        status={status}
        onSelect={changeStatus}
      />

      <ContactsMonthCard month={month} />

      <div ref={listTop} className="scroll-mt-4">
        <ContactsToolbar
        search={search}
        onSearchChange={setSearch}
        status={status}
        onStatusChange={changeStatus}
        source={source}
        onSourceChange={changeSource}
        caption={caption}
        />
      </div>

      {contacts.isError && (
        <div
          role="alert"
          className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-status-danger/40 bg-status-danger/10 px-4 py-3 text-sm text-status-danger"
        >
          <span>Couldn't load contacts. Your connection or sign-in may have dropped.</span>
          <Button variant="outline" className="h-11" onClick={() => contacts.refetch()}>
            Try again
          </Button>
        </div>
      )}

      {!contacts.isError &&
        (!contacts.isLoading && rows.length === 0 ? (
          empty()
        ) : (
          <div className={contacts.isPlaceholderData ? "opacity-60 transition-opacity" : undefined}>
            <ContactsTable
              rows={rows}
              isLoading={contacts.isLoading}
              sort={sort}
              onSort={changeSort}
              timezone={timezone}
              ebrWindowDays={ebrWindowDays}
              onOpen={setOpenContact}
              onToggleMarketing={askToggleMarketing}
            />
          </div>
        ))}

      {!contacts.isError && (
        <ContactsPager
          page={page}
          total={total}
          onPageChange={(next) => {
            setPage(next);
            listTop.current?.scrollIntoView?.({ block: "start", behavior: "smooth" });
          }}
          disabled={contacts.isFetching}
        />
      )}

      <ContactDetailSheet
        contact={openContact}
        onOpenChange={(open) => !open && setOpenContact(null)}
        timezone={timezone}
        ebrWindowDays={ebrWindowDays}
        onToggleMarketing={askToggleMarketing}
      />

      <MarketingExclusionDialog
        target={exclusionTarget}
        onOpenChange={(open) => !open && setExclusionTarget(null)}
        onConfirm={confirmToggleMarketing}
      />
    </div>
  );
}
